import { LogIn, Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { reachesCompany } from '@/auth/access'
import { isInVenueSupportMode } from '@/auth/roles'
import { useEnterVenue } from '@/hooks/useEnterVenue'
import { Button } from '@/components/ui/button'
import type { ButtonProps } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'

interface LoginToVenueButtonProps {
  venueId: string
  /** Longer label for page headers; compact for list rows. */
  size?: 'default' | 'sm'
  /** Visual weight — list rows stay outline; page headers use primary. */
  variant?: ButtonProps['variant']
  className?: string
  /**
   * When true, show the full “Log in to venue” label. When false (list), use
   * the shorter “Log in”.
   */
  fullLabel?: boolean
}

/**
 * Narrow the session to a single venue, so someone whose role reaches the whole
 * company — an organization admin, a Company-wide role, or a super admin already
 * inside the tenant — works with that venue's reach. Hidden for everyone else:
 * staff on assigned venues already work one venue at a time, and move between
 * theirs with the switcher.
 */
export function LoginToVenueButton({
  venueId,
  size = 'default',
  variant = 'outline',
  className,
  fullLabel = false,
}: LoginToVenueButtonProps) {
  const { user } = useAuth()
  const { enter, enteringId, isEntering, error } = useEnterVenue()
  const busy = enteringId === venueId
  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'

  // The API refuses anyone else, so offering the control would only produce a 403.
  if (!reachesCompany(user)) {
    return null
  }

  // Already inside a venue: there is nothing to offer. For this venue an "open"
  // action would invite you into the venue you are looking at from within it, and
  // for any other venue entering again would mean a second overlay — the sidebar
  // picker is how you move between them.
  if (isInVenueSupportMode(user)) {
    return null
  }

  const icon = busy ? <Loader2 className={`${iconClass} animate-spin`} /> : <LogIn className={iconClass} />
  const label = busy ? 'Logging in…' : fullLabel ? 'Log in to venue' : 'Log in'

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        size={size}
        variant={variant}
        className={className}
        disabled={isEntering}
        onClick={() => void enter(venueId)}
      >
        {icon}
        {label}
      </Button>
      {error ? <FormError size="sm" className="max-w-[14rem] justify-end text-right">{error}</FormError> : null}
    </div>
  )
}
